import { updateArrayItem } from '@/util/array';
import examsService from '@/api/exams-service';
import eventBus, { OPEN_SNACKBAR } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { mapErrorsToInputs } from '@/util/forms';

export const initialExamsFilterParams = () => ({});

const state = {
  exams: null,
  examsPagination: {},
  editedExam: {},
  examValidationErrors: {},
  examsFilterParams: initialExamsFilterParams(),
  orderEligibility: null,
};

const getters = {
  examsMap(state) {
    const map = {};

    if (!state.exams) {
      return {};
    }

    for (let i = 0; i < state.exams?.length; i++) {
      map[state.exams[i].id] = state.exams[i];
    }
    return map;
  },
};

const mutations = {
  SET_EXAMS(state, { data, current_page, per_page, total }) {
    state.exams = data;
    state.examsPagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_FILTER_PARAMS(state, params) {
    state.examsFilterParams = params;
  },

  SET_EDITED_EXAM(state, exam) {
    state.examValidationErrors = {};
    state.editedExam = JSON.parse(JSON.stringify(exam));
  },

  UPDATE_EXAM(state, exam) {
    state.exams = updateArrayItem(state.exams, exam);
  },

  SET_EXAM_VALIDATION_ERRORS(state, validationErrors) {
    state.examValidationErrors = validationErrors;
  },

  CLEAR_VALIDATION_ERRORS(state, field) {
    delete state.examValidationErrors[field];
  },

  SET_ORDER_ELIGIBILITY(state, payload) {
    state.orderEligibility = payload
  },
};

const actions = {
  fetchExams({ state, commit }, params = {}) {
    commit('SET_FILTER_PARAMS', params);
    return examsService.getPage(params).then((res) => {
      commit('SET_EXAMS', res.data);
      if (params.page !== state.examsFilterParams.page) {
        window.scrollTo(0, 0);
      }
    });
  },

  editExam({ state, commit }, examId) {
    const exam = state.exams?.find((a) => a.id === examId);
    if (exam) {
      commit('SET_EDITED_EXAM', exam);
      return Promise.resolve(exam);
    }
    return examsService.getById(examId).then((res) => {
      commit('SET_EDITED_EXAM', res.data);
      return res.data;
    });
  },

  updateExam({ commit }, exam) {
    return examsService
      .update(exam)
      .then((res) => {
        commit('UPDATE_EXAM', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('exam_updated'));
      })
      .catch((err) => {
        commit('SET_EXAM_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  orderExams({}, payload) {
    return examsService.
      orderExams(payload)
      .then(() => {
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('training-results.exam_order_success'))
      })
      .catch((err) => {
        if (err.response.status === 422) {
          eventBus.$emit(OPEN_SNACKBAR, i18n.t('training-results.three_months_till_expiration'))
        } else {
          eventBus.$emit(OPEN_SNACKBAR, i18n.t('training-results.exam_order_fail'))
        }
      })
  },

  fetchOrderEligibility({ commit }, userId) {
    return examsService.getOrderEligibility(userId).then((res) => {
      commit('SET_ORDER_ELIGIBILITY', res.data)
      return res.data
    })
  },

  orderExamsUniversal({ }, payload) {
    return examsService
      .orderExamsUniversal(payload)
      .then(() => {
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('training-results.exam_order_success'))
      })
      .catch((err) => {
        const msg = err?.response?.data?.message

        if (msg) {
          const translated = i18n.te(`eligibility.${msg}`)
            ? i18n.t(`eligibility.${msg}`)
            : i18n.t(`training-results.exam_order_fail`)

          eventBus.$emit(OPEN_SNACKBAR, translated)
        } else {
          eventBus.$emit(
            OPEN_SNACKBAR,
            i18n.t('training-results.exam_order_fail')
          )
        }
      })
  },

  clearExamValidationErrors({ commit }, field) {
    commit('CLEAR_VALIDATION_ERRORS', field);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
